"use client";

import { useState } from "react";

import { ChatKit, StartScreenPrompt, useChatKit } from "@openai/chatkit-react";
import type { ColorScheme } from "../hooks/useColorScheme";
import {
  CHATKIT_API_DOMAIN_KEY,
  CHATKIT_API_URL,
  GREETING,
  STARTER_PROMPTS,
  COMPOSER_PLACEHOLDER,
} from "../lib/config";

type ChatKitPanelProps = {
  theme: ColorScheme;
  onThreadChange: (threadId: string | null) => void;
  onResponseCompleted: () => void;
};

export function ChatKitPanel({
  theme,
  onThreadChange,
  onResponseCompleted,
}: ChatKitPanelProps) {
  // Start with no thread to show the start screen
  const [initialThreadId, setInitialThreadId] = useState<string | null>(null);

  const chatkit = useChatKit({
    api: {
      url: CHATKIT_API_URL,
      domainKey: CHATKIT_API_DOMAIN_KEY,
    },
    locale: "en",
    composer: {
      placeholder: COMPOSER_PLACEHOLDER,
    },
    theme: {
      colorScheme: theme,
      color: {
        grayscale: {
          hue: 0,
          tint: 0,
          shade: theme === "dark" ? -1 : -4,
        },
        accent: {
          primary: theme === "dark" ? "#fafafa" : "#18181b",
          level: 1,
        },
      },
      radius: "round",
    },
    startScreen: {
      greeting: GREETING,
      prompts: STARTER_PROMPTS as unknown as StartScreenPrompt[],
    },
    history: {
      enabled: true,
    },
    threadItemActions: {
      feedback: true,
      retry: true,
    },
    initialThread: initialThreadId,
    onResponseEnd: () => {
      onResponseCompleted();
    },
    onThreadChange: async ({ threadId }) => {
      // Update state when thread changes
      // The backend will automatically seed intro messages for new threads
      setInitialThreadId(threadId);
      onThreadChange(threadId ?? null);
    },
    onError: ({ error }) => {
      // ChatKit displays surfaced errors; we keep logging for debugging.
      const isDomainVerificationError =
        error instanceof Error &&
        (error.message.includes("Domain verification failed") ||
          error.message.includes("domain_keys/verify") ||
          error.message.includes("404"));

      if (isDomainVerificationError) {
        const domainKey = CHATKIT_API_DOMAIN_KEY;
        const isLocalhost =
          typeof window !== "undefined" &&
          (window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1");
        const isPlaceholder =
          domainKey.includes("localhost") ||
          domainKey.includes("mars_aisisters_ai");

        if (isLocalhost && isPlaceholder) {
          // In dev mode with placeholder key, domain verification will fail
          // This is expected and can be ignored
          console.warn(
            "ChatKit domain verification skipped in development mode"
          );
          return;
        }

        // Production domain verification failed - this needs to be fixed
        console.error(
          "ChatKit domain verification failed for production domain.\n" +
            "Please ensure:\n" +
            "1. The domain 'mars.aisisters.ai' is registered at https://platform.openai.com/settings/organization/security/domain-allowlist\n" +
            "2. The NEXT_PUBLIC_CHATKIT_API_DOMAIN_KEY in your .env file matches the key provided by OpenAI\n" +
            "3. The domain key is correctly set in docker-compose.yml (should use ${NEXT_PUBLIC_CHATKIT_API_DOMAIN_KEY})\n" +
            `Current domain key prefix: ${domainKey.substring(0, 20)}...`
        );
        // Still log the full error for debugging
        console.error("Full error:", error);
        return;
      }

      console.error("ChatKit error", error);
    },
  });

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden border border-zinc-200/60 bg-white shadow-card dark:border-zinc-800/70 dark:bg-[#27272a]">
      <ChatKit
        control={chatkit.control}
        className="block h-full w-full flex-1"
      />
    </div>
  );
}
